import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navbar,
  Button,
  Input,
  TextArea,
  SlidePanel,
  FieldRow,
  ToggleRow,
  SectionHeader,
  SaveStatusIndicator,
} from '../components';
import { profileService } from '../services';
import { useUser, useAuth } from '../context';
import {
  GATHERING_TYPES,
  BEHAVIORAL_PROMPTS,
  CONTRIBUTION_TAGS,
  LIKELY_TO_OPTIONS,
  ARRIVAL_TIMES,
} from '../constants/partyResumeOptions';

/**
 * Party Preference Page - Optional party preferences
 * Separated from CredentialBuilder to keep onboarding focused on essentials.
 */
export default function PartyPreferencePage() {
  const navigate = useNavigate();
  const { profile, fetchProfile, walletBalance } = useUser();
  const { logout } = useAuth();

  // Parse JSON fields - handles both string and already-parsed object formats
  const parseJSON = (val, defaultVal = {}) => {
    if (!val) return defaultVal;
    if (typeof val === 'object') return val;
    try {
      return JSON.parse(val);
    } catch {
      return defaultVal;
    }
  };

  const extractInstagramHandle = (socialLink) => {
    if (!socialLink) return '';
    const match = socialLink.match(/instagram\.com\/([^/?]+)/);
    return match ? match[1] : '';
  };

  // Form state for "The Basics" fields
  const [formData, setFormData] = useState(() => ({
    location: profile?.location || '',
    pronouns: profile?.pronouns || '',
    pronounsVisible: profile?.pronounsVisible ?? true,
    hedonisticVisible: profile?.hedonisticVisible ?? true,
    gender: profile?.gender || '',
    instagramHandle: extractInstagramHandle(profile?.socialLink),
    drinksAlcohol: profile?.drinksAlcohol || '',
    usesSubstances: profile?.usesSubstances || '',
  }));

  // Extended profile fields
  const existingExtended = parseJSON(profile?.extendedProfile, {});
  const [extendedProfile, setExtendedProfile] = useState({
    contributionTags: existingExtended.contributionTags || [],
    signatureContribution: existingExtended.signatureContribution || '',
    likelyTo: existingExtended.likelyTo || [],
    musicThatMovesYou: existingExtended.musicThatMovesYou || '',
    substancesRelationship: existingExtended.substancesRelationship || '',
    lastThreeGatherings: existingExtended.lastThreeGatherings || '',
    vouchReference: existingExtended.vouchReference || '',
    memorableMoment: existingExtended.memorableMoment || '',
    arrivalTime: existingExtended.arrivalTime || '',
    afterPartyStyle: existingExtended.afterPartyStyle || '',
    rsvpStyle: existingExtended.rsvpStyle || '',
    bonusContent: existingExtended.bonusContent || '',
  });

  const [gatheringTypes, setGatheringTypes] = useState(
    parseJSON(profile?.gatheringTypes, [])
  );

  const [behavioralPrompts, setBehavioralPrompts] = useState(
    parseJSON(profile?.behavioralPrompts, {})
  );

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [activePanel, setActivePanel] = useState(null);

  // Mobile sticky bottom bar scroll tracking
  const [showBottomBar, setShowBottomBar] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      const atBottom = (window.innerHeight + currentY) >= (document.documentElement.scrollHeight - 200);
      if (Math.abs(delta) > 5) {
        setShowBottomBar(atBottom || delta < 0 || currentY < 10);
        lastScrollY.current = currentY;
      } else if (atBottom) {
        setShowBottomBar(true);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track if we've initialized from profile (only sync once on mount)
  const hasInitialized = useRef(false);

  // Sync state when profile loads
  useEffect(() => {
    if (profile && !hasInitialized.current) {
      hasInitialized.current = true;

      const safeParseJSON = (val, defaultVal) => {
        if (!val) return defaultVal;
        if (typeof val === 'object') return val;
        try {
          return JSON.parse(val);
        } catch {
          return defaultVal;
        }
      };

      const extended = safeParseJSON(profile.extendedProfile, {});

      setFormData({
        location: profile.location || '',
        pronouns: profile.pronouns || '',
        pronounsVisible: profile.pronounsVisible ?? true,
        hedonisticVisible: profile.hedonisticVisible ?? true,
        gender: profile.gender || '',
        instagramHandle: extractInstagramHandle(profile.socialLink),
        drinksAlcohol: profile.drinksAlcohol || '',
        usesSubstances: profile.usesSubstances || '',
      });

      setExtendedProfile({
        contributionTags: extended.contributionTags || [],
        signatureContribution: extended.signatureContribution || '',
        likelyTo: extended.likelyTo || [],
        musicThatMovesYou: extended.musicThatMovesYou || '',
        substancesRelationship: extended.substancesRelationship || '',
        lastThreeGatherings: extended.lastThreeGatherings || '',
        vouchReference: extended.vouchReference || '',
        memorableMoment: extended.memorableMoment || '',
        arrivalTime: extended.arrivalTime || '',
        afterPartyStyle: extended.afterPartyStyle || '',
        rsvpStyle: extended.rsvpStyle || '',
        bonusContent: extended.bonusContent || '',
      });

      setGatheringTypes(safeParseJSON(profile.gatheringTypes, []));
      setBehavioralPrompts(safeParseJSON(profile.behavioralPrompts, {}));
    }
  }, [profile]);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleExtendedChange = (e) => {
    const { name, value } = e.target;
    setExtendedProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBehavioralChange = (key, value) => {
    setBehavioralPrompts((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleGatheringType = (type) => {
    setGatheringTypes((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(type) ? arr.filter((t) => t !== type) : [...arr, type];
    });
  };

  const toggleContributionTag = (tag) => {
    setExtendedProfile((prev) => {
      const arr = Array.isArray(prev.contributionTags) ? prev.contributionTags : [];
      return {
        ...prev,
        contributionTags: arr.includes(tag) ? arr.filter((t) => t !== tag) : [...arr, tag],
      };
    });
  };

  const toggleLikelyTo = (option) => {
    setExtendedProfile((prev) => {
      const arr = Array.isArray(prev.likelyTo) ? prev.likelyTo : [];
      return {
        ...prev,
        likelyTo: arr.includes(option) ? arr.filter((o) => o !== option) : [...arr, option],
      };
    });
  };

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const autoSaveTimerRef = useRef(null);
  const statusResetRef = useRef(null);
  const isSavingProfileRef = useRef(false);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (statusResetRef.current) clearTimeout(statusResetRef.current);
    };
  }, []);

  const buildProfileData = useCallback(() => ({
    socialLink: formData.instagramHandle
      ? `https://instagram.com/${formData.instagramHandle.replace('@', '')}`
      : null,
    location: formData.location || null,
    pronouns: formData.pronouns || null,
    pronounsVisible: formData.pronounsVisible,
    hedonisticVisible: formData.hedonisticVisible,
    gender: formData.gender || null,
    drinksAlcohol: formData.drinksAlcohol || null,
    usesSubstances: formData.usesSubstances || null,
    gatheringTypes: JSON.stringify(gatheringTypes),
    behavioralPrompts: JSON.stringify(behavioralPrompts),
    // Merge onto the currently-persisted blob so keys this page doesn't edit —
    // notably the Spotify `anthem` set in the intake flow — survive the save.
    // The backend replaces extended_profile wholesale, so anything omitted here
    // would be wiped. Edited whitelist fields override; everything else is kept.
    extendedProfile: JSON.stringify({ ...parseJSON(profile?.extendedProfile, {}), ...extendedProfile }),
  }), [formData, gatheringTypes, behavioralPrompts, extendedProfile, profile]);

  const saveProfile = useCallback(async () => {
    if (isSavingProfileRef.current) return;

    isSavingProfileRef.current = true;
    setAutoSaveStatus('saving');

    try {
      const profileData = buildProfileData();
      const result = await profileService.updateProfile(profileData);

      if (result.success) {
        setAutoSaveStatus('saved');
      } else {
        setAutoSaveStatus('error');
      }
    } catch {
      setAutoSaveStatus('error');
    } finally {
      isSavingProfileRef.current = false;
      if (statusResetRef.current) clearTimeout(statusResetRef.current);
      statusResetRef.current = setTimeout(() => setAutoSaveStatus('idle'), 3000);
    }
  }, [buildProfileData]);

  const debouncedSaveProfile = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => saveProfile(), 500);
  }, [saveProfile]);

  // Auto-save on toggle changes
  const prevTogglesRef = useRef(null);
  useEffect(() => {
    const toggleState = `${formData.pronounsVisible}-${formData.hedonisticVisible}`;
    if (prevTogglesRef.current !== null && prevTogglesRef.current !== toggleState) {
      debouncedSaveProfile();
    }
    prevTogglesRef.current = toggleState;
  }, [formData.pronounsVisible, formData.hedonisticVisible, debouncedSaveProfile]);

  // Wrap panel close to auto-save
  const closePanelAndSave = useCallback(() => {
    setActivePanel(null);
    saveProfile();
  }, [saveProfile]);

  const handleSaveAndContinue = async () => {
    setIsLoading(true);
    try {
      await saveProfile();
      await fetchProfile();
      const dashboard = profile?.initialMode === 'host' ? '/host' : '/guest';
      navigate(dashboard);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    const dashboard = profile?.initialMode === 'host' ? '/host' : '/guest';
    navigate(dashboard);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Helper to get display value for multi-select fields
  const getMultiSelectDisplay = (items, max = 3) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    if (items.length <= max) return items.join(', ');
    return `${items.slice(0, max).join(', ')} +${items.length - max}`;
  };

  const getSelectLabel = (value, options) => {
    const opt = options.find((o) => o.value === value);
    return opt?.label || value;
  };

  return (
    <div className="min-h-screen">
      <Navbar user={profile} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/')} />

      <main className="max-w-lg mx-auto pb-32">
        {/* Header */}
        <div className="p-6 text-center border-b border-gray-200 bg-white">
          <h1 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-1">
            Party Preferences
          </h1>
          <p className="font-mono text-xs text-gray-500">
            More info = better parties
          </p>
          <div className="h-4">
            <SaveStatusIndicator status={autoSaveStatus} />
          </div>
        </div>

        {/* The Basics Section */}
        <SectionHeader title="The Basics" />
        <div className="bg-white">
          <FieldRow
            label="Location"
            value={formData.location}
            placeholder="City or area"
            onClick={() => setActivePanel('location')}
          />
          <FieldRow
            label="Pronouns"
            value={formData.pronouns}
            placeholder="e.g., she/her"
            onClick={() => setActivePanel('pronouns')}
            rightElement={
              <span className="font-mono text-xs text-gray-400">
                {formData.pronounsVisible ? 'Visible' : 'Hidden'} &rsaquo;
              </span>
            }
          />
          <FieldRow
            label="Gender"
            value={formData.gender ? (formData.gender === 'prefer_not_to_say' ? 'Prefer not to say' : formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1)) : null}
            onClick={() => setActivePanel('gender')}
          />
          <FieldRow
            label="Instagram"
            value={formData.instagramHandle ? `@${formData.instagramHandle}` : null}
            onClick={() => setActivePanel('instagram')}
          />
          <FieldRow
            label="Drinks Alcohol"
            value={formData.drinksAlcohol ? formData.drinksAlcohol.charAt(0).toUpperCase() + formData.drinksAlcohol.slice(1).replace(/_/g, ' ') : null}
            onClick={() => setActivePanel('drinksAlcohol')}
          />
          <FieldRow
            label="Party Substances"
            value={formData.usesSubstances ? formData.usesSubstances.charAt(0).toUpperCase() + formData.usesSubstances.slice(1).replace(/_/g, ' ') : null}
            onClick={() => setActivePanel('usesSubstances')}
          />
        </div>

        {/* Gathering Types */}
        <SectionHeader title="Gathering Types" subtitle="What excites you?" />
        <div className="bg-white">
          <FieldRow
            label="Types of Gatherings"
            value={getMultiSelectDisplay(gatheringTypes)}
            placeholder="Select what interests you"
            onClick={() => setActivePanel('gatheringTypes')}
          />
        </div>

        {/* What You Bring */}
        <SectionHeader title="What You Bring" />
        <div className="bg-white">
          <FieldRow
            label="Contribution Tags"
            value={getMultiSelectDisplay(extendedProfile.contributionTags)}
            placeholder="What do you bring?"
            onClick={() => setActivePanel('contributions')}
          />
          <FieldRow
            label="Music That Moves You"
            value={extendedProfile.musicThatMovesYou}
            onClick={() => setActivePanel('music')}
          />
          <FieldRow
            label="Arrival Style"
            value={extendedProfile.arrivalTime ? getSelectLabel(extendedProfile.arrivalTime, ARRIVAL_TIMES) : null}
            onClick={() => setActivePanel('arrivalTime')}
          />
        </div>

        {/* Optional Prompts */}
        <SectionHeader title="Optional Prompts" subtitle="Help hosts know you better" />
        <div className="bg-white">
          {BEHAVIORAL_PROMPTS.map((prompt) => (
            <FieldRow
              key={prompt.key}
              label={prompt.label}
              value={behavioralPrompts[prompt.key]}
              placeholder={prompt.placeholder}
              onClick={() => setActivePanel(`prompt_${prompt.key}`)}
            />
          ))}
        </div>

      </main>

      {/* Sticky bottom bar */}
      <div
        className={`
          fixed bottom-0 left-0 right-0 z-40
          transition-transform duration-300 ease-in-out
          ${showBottomBar ? 'translate-y-0' : 'translate-y-full'}
        `}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="max-w-lg mx-auto bg-white border-t-2 border-black shadow-[0_-4px_12px_rgba(0,0,0,0.1)] px-4 pt-3 pb-3 space-y-3">
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            disabled={isLoading}
            onClick={handleSaveAndContinue}
          >
            {isLoading ? 'SAVING...' : 'SAVE & CONTINUE'}
          </Button>
          <button
            type="button"
            onClick={handleSkip}
            className="w-full text-center font-mono text-xs text-gray-500 uppercase tracking-wider hover:text-black transition-colors py-2"
          >
            SKIP FOR NOW
          </button>
        </div>
      </div>

      {/* Slide Panels */}
      <SlidePanel
        isOpen={activePanel === 'location'}
        onClose={closePanelAndSave}
        title="Location"
      >
        <Input
          name="location"
          value={formData.location}
          onChange={handleInputChange}
          placeholder="City or area"
          autoFocus
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'pronouns'}
        onClose={closePanelAndSave}
        title="Pronouns"
      >
        <Input
          name="pronouns"
          value={formData.pronouns}
          onChange={handleInputChange}
          placeholder="e.g., she/her, he/him, they/them"
          className="mb-4"
          autoFocus
        />
        <ToggleRow
          label="Visible on Profile"
          checked={formData.pronounsVisible}
          onChange={handleInputChange}
          name="pronounsVisible"
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'gender'}
        onClose={closePanelAndSave}
        title="Gender"
      >
        <div className="space-y-2">
          {[
            { value: 'man', label: 'Man' },
            { value: 'woman', label: 'Woman' },
            { value: 'non-binary', label: 'Non-binary' },
            { value: 'prefer_not_to_say', label: 'Prefer not to say' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, gender: opt.value }));
                closePanelAndSave();
              }}
              className={`
                w-full p-4 text-left border-2 transition-colors
                ${formData.gender === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-black'
                }
              `}
            >
              <span className="font-mono text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'drinksAlcohol'}
        onClose={closePanelAndSave}
        title="Do You Drink Alcohol?"
      >
        <div className="space-y-2">
          {[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'prefer_not_to_say', label: 'Prefer not to say' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, drinksAlcohol: opt.value }));
                closePanelAndSave();
              }}
              className={`
                w-full p-4 text-left border-2 transition-colors
                ${formData.drinksAlcohol === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-black'
                }
              `}
            >
              <span className="font-mono text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'usesSubstances'}
        onClose={closePanelAndSave}
        title="Party Substances"
      >
        <div className="space-y-2">
          {[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'prefer_not_to_say', label: 'Prefer not to say' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, usesSubstances: opt.value }));
                closePanelAndSave();
              }}
              className={`
                w-full p-4 text-left border-2 transition-colors
                ${formData.usesSubstances === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-black'
                }
              `}
            >
              <span className="font-mono text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'instagram'}
        onClose={closePanelAndSave}
        title="Instagram"
      >
        <div className="flex items-center">
          <span className="font-mono text-sm text-gray-500 mr-2">@</span>
          <Input
            name="instagramHandle"
            value={formData.instagramHandle}
            onChange={handleInputChange}
            placeholder="yourhandle"
            className="flex-1"
            autoFocus
          />
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'gatheringTypes'}
        onClose={closePanelAndSave}
        title="Gathering Types"
      >
        <p className="font-mono text-xs text-gray-500 mb-4">
          Select all that excite you
        </p>
        <div className="space-y-6">
          {Object.entries(GATHERING_TYPES).map(([key, category]) => (
            <div key={key}>
              <h3 className="font-mono text-xs font-bold uppercase text-gray-700 mb-3">
                {category.label}
              </h3>
              {key === 'hedonistic' && (
                <div className="mb-3">
                  <ToggleRow
                    label="Visible on Profile"
                    description="Hide these selections from your public profile"
                    checked={formData.hedonisticVisible}
                    onChange={handleInputChange}
                    name="hedonisticVisible"
                  />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {category.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleGatheringType(option)}
                    className={`
                      px-3 py-2 font-mono text-xs uppercase border-2 transition-colors
                      ${Array.isArray(gatheringTypes) && gatheringTypes.includes(option)
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-black border-gray-300 hover:border-black'
                      }
                    `}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'contributions'}
        onClose={closePanelAndSave}
        title="What You Bring"
      >
        <p className="font-mono text-xs text-gray-500 mb-4">
          Beyond your presence, what do you bring?
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          {CONTRIBUTION_TAGS.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleContributionTag(tag)}
              className={`
                px-3 py-2 font-mono text-xs uppercase border-2 transition-colors
                ${Array.isArray(extendedProfile.contributionTags) && extendedProfile.contributionTags.includes(tag)
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-black border-gray-300 hover:border-black'
                }
              `}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="mb-4">
          <label className="block font-mono text-xs font-bold uppercase text-gray-700 mb-2">
            Signature Contribution
          </label>
          <Input
            name="signatureContribution"
            value={extendedProfile.signatureContribution}
            onChange={handleExtendedChange}
            placeholder="Your signature move..."
          />
        </div>

        <div>
          <label className="block font-mono text-xs font-bold uppercase text-gray-700 mb-3">
            More likely to...
          </label>
          <div className="flex flex-wrap gap-2">
            {LIKELY_TO_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => toggleLikelyTo(option)}
                className={`
                  px-3 py-2 font-mono text-xs uppercase border-2 transition-colors
                  ${Array.isArray(extendedProfile.likelyTo) && extendedProfile.likelyTo.includes(option)
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-black border-gray-300 hover:border-black'
                  }
                `}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'music'}
        onClose={closePanelAndSave}
        title="Music That Moves You"
      >
        <Input
          name="musicThatMovesYou"
          value={extendedProfile.musicThatMovesYou}
          onChange={handleExtendedChange}
          placeholder="Genres, artists, vibes..."
          autoFocus
        />
      </SlidePanel>

      <SlidePanel
        isOpen={activePanel === 'arrivalTime'}
        onClose={closePanelAndSave}
        title="Arrival Style"
      >
        <div className="space-y-2">
          {ARRIVAL_TIMES.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setExtendedProfile((prev) => ({ ...prev, arrivalTime: opt.value }));
                closePanelAndSave();
              }}
              className={`
                w-full p-4 text-left border-2 transition-colors
                ${extendedProfile.arrivalTime === opt.value
                  ? 'border-black bg-black text-white'
                  : 'border-gray-200 hover:border-black'
                }
              `}
            >
              <span className="font-mono text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </SlidePanel>

      {/* Behavioral Prompt Panels */}
      {BEHAVIORAL_PROMPTS.map((prompt) => (
        <SlidePanel
          key={prompt.key}
          isOpen={activePanel === `prompt_${prompt.key}`}
          onClose={closePanelAndSave}
          title={prompt.label}
        >
          <TextArea
            value={behavioralPrompts[prompt.key] || ''}
            onChange={(e) => handleBehavioralChange(prompt.key, e.target.value)}
            placeholder={prompt.placeholder}
            rows={4}
            maxLength={500}
            autoFocus
          />
        </SlidePanel>
      ))}
    </div>
  );
}
